<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import ThreadList, {
  ThreadListInstance,
} from "@/components/mobile/ThreadList.vue";
import { ref, watch, computed } from "vue";
import { ActionSheet } from "vant";
import { readPowerCubePage } from "@/apis/user";
import { PowerCube } from "@/types/entity";
import { TransactionType, PowerCubeTransactionCategory } from "@/types/user";
import { useI18n } from "vue-i18n";
import { routerBack } from "@/use/go";

// States
const { t } = useI18n();
const currentTab = ref(0); // 0: 全部, 1: 收入, 2: 支出
const selectedCategory = ref<PowerCubeTransactionCategory | undefined>(
  undefined
);
const excludeZero = ref(false);
const powerCubeList = ref<PowerCube[]>([]);
const threadListRef = ref<ThreadListInstance | null>(null);

// Tab configuration
const tabs = computed(() => [
  { title: t("PowerCube.all"), type: undefined },
  { title: t("PowerCube.income"), type: TransactionType.Income },
  { title: t("PowerCube.expense"), type: TransactionType.Expense },
]);

const showCategoryActionSheet = ref(false);

const categoryActions = computed(() => [
  { name: t("PowerCube.category.all"), value: undefined },
  ...Object.values(PowerCubeTransactionCategory)
    .filter(
      (category) =>
        Number(category) > 0 &&
        ![
          PowerCubeTransactionCategory.Chat,
          PowerCubeTransactionCategory.DrawImage,
          PowerCubeTransactionCategory.DrawAvatar,
          PowerCubeTransactionCategory.VoteRanking,
        ].includes(category as PowerCubeTransactionCategory)
    )
    .map((category) => ({
      name: t(`PowerCube.category.${category}`),
      value: category,
    })),
]);
// Methods
const fetchPowerCubeList = async (): Promise<boolean> => {
  try {
    let tmpTab = currentTab.value;
    const { length } = powerCubeList.value;
    if (length % 20 !== 0) return false;
    const page = length / 20 + 1;

    const response = await readPowerCubePage({
      pagination: {
        page,
        perPage: 20,
      },
      filter: {
        type: tabs.value[currentTab.value].type,
        category: selectedCategory.value,
        excludeZero: excludeZero.value ? 1 : 0,
      },
      sort: {
        field: "dtCreate",
        order: "desc",
      },
    });

    const newItems = response.page.data;
    if (tmpTab !== currentTab.value) return true;

    powerCubeList.value.push(...newItems);

    return newItems.length === 20;
  } catch (error) {
    console.error("Failed to fetch power cube list:", error);
    return false;
  }
};

const onCategorySelect = async (action: {
  name: string;
  value?: PowerCubeTransactionCategory;
}) => {
  selectedCategory.value = action.value;
  showCategoryActionSheet.value = false;
  currentTab.value = 0;
  await refreshList();
};

const refreshList = async () => {
  powerCubeList.value = [];
  threadListRef.value?.resetScrollLoadingState();
};

const formatAmount = (amount: number, type: TransactionType) => {
  const prefix = type === TransactionType.Income ? "+" : "-";
  return `${prefix}${Math.abs(amount)}`;
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return (
    date.toLocaleDateString() +
    " " +
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
};

watch([currentTab, selectedCategory, excludeZero], () => {
  refreshList();
});
</script>

<template>
  <div class="h-full grid grid-rows-[auto_auto_1fr] relative">
    <!-- Header -->
    <threads-nav :go-back="routerBack" show-back>
      <template #center>
        <div class="font-bold">{{ $t("PowerCube.title") }}</div>
      </template>
    </threads-nav>
    <div class="flex gap-2 justify-between px-4">
      <div class="flex gap-1 items-center">
        <button
          v-for="i in [0, 1, 2]"
          :key="i"
          :class="['btn btn-xs', currentTab === i && 'btn-primary']"
          @click="currentTab = i"
        >
          {{ $t(`PowerCube.tab.${i}`) }}
        </button>
      </div>
      <div class="flex items-center gap-2">
        <van-checkbox
          v-model="excludeZero"
          icon-size="12px"
          shape="square"
          class="text-xs"
        >
          {{ $t("PowerCube.excludeZero") }}
        </van-checkbox>

        <button
          :class="['btn btn-xs btn-outline', selectedCategory && 'btn-primary']"
          @click="showCategoryActionSheet = true"
        >
          {{ $t(`PowerCube.category.${selectedCategory}`) }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="w-full overflow-hidden">
      <!-- List -->
      <thread-list
        ref="threadListRef"
        :on-pull-load="refreshList"
        :on-scroll-load="fetchPowerCubeList"
        class="h-full"
      >
        <div class="space-y-2 p-4">
          <div
            v-for="item in powerCubeList"
            :key="item.id"
            class="bg-base-100 rounded-lg px-4 py-2 shadow-sm border"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <div class="font-medium mb-1">
                  {{ $t(`PowerCube.category.${item.category}`) }}
                </div>
                <div class="text-xs text-base-content/60">
                  {{ formatDate(item.dtCreate) }}
                </div>
              </div>
              <div class="text-right">
                <div v-if="item.amount === 0" class="flex gap-1 items-center">
                  <div class="badge badge-success badge-outline badge-sm">
                    {{ $t("Message.memberBenefit") }}
                  </div>
                  <div class="badge badge-success badge-outline badge-sm">
                    {{ $t("Message.free") }}
                  </div>
                </div>
                <div
                  v-else
                  class="text-base font-bold"
                  :class="{
                    'text-success': item.type === TransactionType.Income,
                    'text-error': item.type === TransactionType.Expense,
                  }"
                >
                  {{ formatAmount(item.amount, item.type) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </thread-list>
    </div>

    <!-- Category Selection ActionSheet -->
    <action-sheet
      v-model:show="showCategoryActionSheet"
      :actions="categoryActions"
      @select="onCategorySelect"
      :title="$t('PowerCube.selectCategory')"
    />
  </div>
</template>
